import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const LABELS = {
  retinoid: "Retinoid",
  vitamin_c: "Vitamin C",
  exfoliating_cleanser: "Exfoliating cleanser",
};

// Items that should NOT surface on the dashboard while a user is in Skin
// Resilience — they're not appropriate to ramp up yet. The user's tracker
// data is preserved (just hidden here) so it reappears when they move to MAC.
const RESILIENCE_HIDDEN = new Set(["retinoid", "vitamin_c"]);

/**
 * Tiny dashboard widget showing today's tracker suggestions (only the active
 * items with cadence info). Renders nothing if no tracker items exist.
 */
export default function TrackerWidget() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/tracker").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const inResilience = (user?.current_phase || "skin_resilience") === "skin_resilience";
  const allEntries = Object.entries(data.items || {});
  const items = inResilience
    ? allEntries.filter(([k]) => !RESILIENCE_HIDDEN.has(k))
    : allEntries;
  if (items.length === 0) {
    // Empty-state CTA — only show in MAC+; during Resilience there's nothing
    // appropriate to ramp up yet, so we hide the widget entirely.
    if (inResilience) return null;
    return (
      <div className="aa-card p-5" data-testid="dashboard-tracker-widget-empty">
        <div className="aa-eyebrow mb-2">Application tracker</div>
        <div className="text-sm text-[var(--aa-text-2)] mb-3">Track a slow ramp-up (retinoid, vitamin C, exfoliating cleanser) and we'll keep the schedule straight.</div>
        <Link to="/tracker" className="aa-cta-secondary text-sm" data-testid="dashboard-tracker-cta">Set up tracker →</Link>
      </div>
    );
  }
  return (
    <div className="aa-card p-5" data-testid="dashboard-tracker-widget">
      <div className="aa-eyebrow mb-3">Today's tracker</div>
      <ul className="space-y-2">
        {items.map(([k, v]) => {
          const sugg = v.suggestion || {};
          return (
            <li key={k} className="flex items-start justify-between gap-3 text-sm">
              <div>
                <div className="font-display text-base">{LABELS[k] || v.label}</div>
                {data.suggestions_enabled && sugg.cadence_phase && (
                  <div className="text-xs text-[var(--aa-text-3)] leading-relaxed">{sugg.cadence_phase}</div>
                )}
              </div>
              <div className="text-right shrink-0">
                {data.suggestions_enabled ? (
                  sugg.is_due_today ? (
                    <span className="aa-pill verdict-green text-xs px-2 py-0.5" data-testid={`widget-due-${k}`}>Due today</span>
                  ) : (
                    <span className="aa-eyebrow text-[10px]" data-testid={`widget-next-${k}`}>Next: {sugg.next_due}</span>
                  )
                ) : (
                  <span className="aa-eyebrow text-[10px]">{(v.logs || []).length} logs</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <Link to="/tracker" className="aa-cta-secondary text-sm mt-4 inline-flex" data-testid="dashboard-tracker-open">Open tracker →</Link>
    </div>
  );
}
